import React, { useState, useMemo } from "react";
import { useUser } from "../../contexts/UserContext";
import { apiUtils } from "../../utils/apiUtils";

// Common IANA timezones used if the browser can't enumerate them.
const FALLBACK_TZS = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Moscow",
  "Africa/Lagos", "Africa/Johannesburg", "Africa/Cairo", "Asia/Dubai", "Asia/Kolkata",
  "Asia/Singapore", "Asia/Shanghai", "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland",
];

function getTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      const list = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(list) && list.length) {
        return ["UTC", ...list.filter((z) => z !== "UTC")];
      }
    }
  } catch { /* fall through */ }
  return FALLBACK_TZS;
}

/**
 * GeneralSettings - the user's account settings.
 * Persists to PUT /api/users/me (name, phone, timezone, notification prefs).
 */
export default function GeneralSettings() {
  const { user, updateUser } = useUser();
  const timezones = useMemo(getTimezones, []);
  const guessedTz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; }
  }, []);

  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    timezone: user?.timezone || guessedTz || "UTC",
    emailAlerts: user?.notificationPreferences?.emailAlerts ?? true,
  });
  const [status, setStatus] = useState({ saving: false, ok: false, error: "" });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setStatus({ saving: true, ok: false, error: "" });
    try {
      const { data } = await apiUtils.put("/api/users/me", {
        name: form.name,
        phone: form.phone,
        timezone: form.timezone,
        notificationPreferences: { emailAlerts: form.emailAlerts },
      });
      if (data && (data.id || data._id)) updateUser(data);
      setStatus({ saving: false, ok: true, error: "" });
      setTimeout(() => setStatus((s) => ({ ...s, ok: false })), 2500);
    } catch (e) {
      setStatus({ saving: false, ok: false, error: e?.response?.data?.error || e.message || "Couldn't save changes" });
    }
  };

  const inputCls =
    "w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30";

  const Section = ({ title, desc, children }) => (
    <div className="border-t border-border pt-5 first:border-t-0 first:pt-0">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {desc && <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>}
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Section title="Profile" desc="Your account details.">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Full name</label>
          <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
          <input className={`${inputCls} bg-muted text-muted-foreground`} value={user?.email || ""} disabled />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Phone <span className="text-muted-foreground">(optional)</span>
          </label>
          <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 555 000 0000" />
        </div>
      </Section>

      <Section title="Timezone" desc="Scheduled monitoring runs at your local time in this timezone.">
        <select className={inputCls} value={form.timezone} onChange={(e) => set("timezone", e.target.value)}>
          {timezones.map((tz) => (
            <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
          ))}
        </select>
      </Section>

      <Section title="Notifications" desc="How we reach you about reputation risks.">
        <label className="flex items-center justify-between gap-4">
          <span className="min-w-0">
            <span className="block text-sm text-foreground">Email alerts</span>
            <span className="block text-xs text-muted-foreground">Email me when a monitored account hits at-risk or critical.</span>
          </span>
          <input
            type="checkbox"
            className="w-4 h-4 accent-brand-600 shrink-0"
            checked={form.emailAlerts}
            onChange={(e) => set("emailAlerts", e.target.checked)}
          />
        </label>
      </Section>

      {status.error && <p className="text-sm text-red-600">{status.error}</p>}

      <div className="pt-2">
        <button
          onClick={handleSave}
          disabled={status.saving}
          className={`w-full rounded-lg py-2.5 px-4 text-sm font-semibold text-white transition-colors disabled:bg-neutral-400 ${
            status.ok ? "bg-green-600" : "bg-brand-600 hover:bg-brand-700"
          }`}
        >
          {status.saving ? "Saving…" : status.ok ? "Saved" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
